import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, Injectable, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { ModelFilterComponent } from '@app/filters/filter-bar/model-filter/model-filter.component';
import { ExtendedToggleComponent } from '@app/shared/components/extended-toggle/extended-toggle.component';
import { IconBagErrorComponent } from '@app/shared/icons/components/icon-bag-error/icon-bag-error.component';
import { IconBagComponent } from '@app/shared/icons/components/icon-bag/icon-bag.component';
import { AppStore } from '@app/shared/stores/app.store';
import { UserPreferencesStore } from '@app/shared/stores/user-preferences.store';
import { BrandEnum } from '@infrastructure/brand.enum';
import { BagDisplayModel } from '@models/bag-display-model';
import { ClientDisplayModeEnum } from '@models/client-display-mode.enum';
import { InspirationGroupModel } from '@models/inspiration-group';
import { UserDataModel } from '@models/user-data-model';
import { TranslatePipe } from '@ngx-translate/core';
import { BrandModeService } from '@services/brandmode.service';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { LoginService } from '@services/login.service';
import { NavigateService } from '@services/navigate.service';
import { OptionsDropdownService } from '@services/options-dropdown.service';
import { OrderOnBehalfService } from '@services/order-on-behalf.service';
import { TagsService } from '@services/tags.service';
import { TermsService } from '@services/terms.service';
import { ThemeService } from '@services/theme.service';
import { UserService } from '@services/user.service';
import { AppState } from '@shared/app-state';
import { PermissionConstants } from '@shared/permission-constants';
import { BadgePopTransition } from '@shared/transitions/badge-pop.transition';
import { FadeInOutTransition } from '@shared/transitions/fade-in-out.transition';
import { UserTypeConstant } from '@shared/user-type-constants';
import { pulseAnimation } from 'angular-animations';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, Observable, Subscription } from 'rxjs';
import { OptionsDropdownComponent } from './components/options-dropdown/options-dropdown.component';
import { SelectedCustomerBarComponent } from './components/selected-customer-bar/selected-customer-bar.component';
import { log } from 'numeric';

@Injectable({
	providedIn: 'root'
})
export class SearchService {

	searchSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

	search(searchTerm: string) {
		this.searchSubject.next(searchTerm);
	}

}

@Component({
	selector: 'menu-header',
	templateUrl: 'header.html',
	styleUrls: ['./header.scss'],
	animations: [
		FadeInOutTransition(),
		BadgePopTransition(),
		pulseAnimation({ duration: 300, scale: 0.85 })
	],
	host: {
		"[@fadeInOutTransition]": ''
	},
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslatePipe, CommonModule, OptionsDropdownComponent, ExtendedToggleComponent, SelectedCustomerBarComponent, IconBagComponent, IconBagErrorComponent, ModelFilterComponent, FormsModule]
})
export class HeaderComponent implements OnInit, OnDestroy {

	appStore = inject(AppStore)
	themeService = inject(ThemeService);
	enduserDisplayService = inject(EnduserDisplayService);
	searchService = inject(SearchService);
	breakpointObserver = inject(BreakpointObserver);
	userPreferencesStore = inject(UserPreferencesStore);

	@ViewChild('search', { static: false }) searchElement: ElementRef<HTMLInputElement>;

	searchTerm: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

	clientDisplayMode: ClientDisplayModeEnum | undefined;

	public addToBagInProgress: boolean = false;

	toggleMini: boolean = false;
	animationSearchState = 'start';
	animationImageState = 'start';
	animationCloseSearchState = 'start';
	showSearch: boolean = false;
	public userCanOrderOnBehalf: boolean = false;
	public isLoggedIn: Observable<boolean>;
	private subscriptions: Array<Subscription> = [];
	activeIcon: string;
	bagCount: number;
	bagCountSpecified: string;
	user: UserDataModel | null;
	showFilterBar: boolean;
	showTermsInDropdownMenu: boolean = false;
	showNotificationBullet: boolean;
	public hasBagErrors: boolean = false;
	public showBagErrorItems: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public consultantOrChain: boolean = false;

	isMenuOpen: boolean = false;

	constructor(
		public appState: AppState,
		public brandModeService: BrandModeService,
		public orderOnBehalfService: OrderOnBehalfService,
		private loginService: LoginService,
		private navigateService: NavigateService,
		private optionsDropDownService: OptionsDropdownService,
		private router: Router,
		private userService: UserService,
		private tagsService: TagsService,
		private termsService: TermsService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private destroyRef: DestroyRef
	) {
		this.isLoggedIn = this.loginService.IsLoggedInObservable();

		this.subscriptions.push(this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe(event => {
			this.showFilterBar = event.url === "/filterlist";
		}));

		this.appState.hasActiveAddToBagExecutionsStatus.subscribe((value) => {
			this.addToBagInProgress = value;
		});

		this.tagsService.shouldShowObservable().subscribe(value => this.showFilterBar = value);

		this.appState.bagErrors.subscribe(errors => {
			this.hasBagErrors = errors.length > 0;
		});

		this.searchTerm.pipe(debounceTime(500), distinctUntilChanged()).subscribe(searchTerm => {
			if (searchTerm && searchTerm.length > 2) {
				this.searchService.search(searchTerm);

				this.router.navigate(['/search', searchTerm], { onSameUrlNavigation: 'reload' });
			}
		});
	}

	async ngOnInit() {
		this.loginService.IsLoggedInObservable().subscribe(async l => {
			this.user = await this.userService.GetUser();

			if (this.user) {
				this.userCanOrderOnBehalf = this.user.UserPermissions?.some(x => x === PermissionConstants.CanOrderOnBehalf) ?? false;
				this.consultantOrChain = this.user.isAdmin || this.userCanOrderOnBehalf || this.user.AdminOnChain;
				this.initTermsData();

				this.userPreferencesStore.setContactCode(this.user.ContactCode);
			}
		});

		this.clientDisplayMode = this.enduserDisplayService.IsClient() ? ClientDisplayModeEnum.client : ClientDisplayModeEnum.display;

		this.appState.bagDisplay.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(bagUpdatemodel => {
			this.setBagCount(bagUpdatemodel);
		});

		this.breakpointObserver.observe(`(max-width: 680px)`)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(state => {
				if (!state.matches) {
					if (this.showSearch) {
						this.toggleSearch()
					}
				}
			});

		this.breakpointObserver.observe(`(max-width: 554px)`)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(state => {
				if (!state.matches) {
					this.toggleMini = false;
				}
				else {
					this.toggleMini = true;
				}
			});
	}

	// BAG RELATED

	private setBagCount(bagUpdate: BagDisplayModel) {
		const framesCount = bagUpdate.FullFrames;
		const miscCount = bagUpdate.AccessoriesAndTools;
		const plusStr = framesCount > 0 && miscCount > 0 ? "+" : "";

		const framesCountStr = framesCount > 0 ? "" + framesCount : "";
		const miscCountStr = miscCount > 0 ? "" + miscCount : "";

		this.bagCountSpecified = (framesCountStr + plusStr + miscCountStr);
		this.bagCount = framesCount + miscCount;
	}

	animateIcon(icon: string) {
		let result = icon === this.activeIcon;

		return result;
	}

	goToBag() {
		if (this.addToBagInProgress) {
			return;
		}

		this.activateTitanium();
		this.router.navigate(['/bag']);
	}

	toggleShowBagErrorItems(event: MouseEvent) {
		if (this.showBagErrorItems.getValue() && (event.target as HTMLElement).closest('icon-bag-error .content-wrapper')) {
			return;
		}

		const show = !this.showBagErrorItems.value;
		this.showBagErrorItems.next(show);
	}

	// END BAG RELATED

	applyFilter($event: any) {
		this.searchTerm.next(this.searchElement.nativeElement.value);
	}

	clientDisplayValueChanged($event: number) {
		this.enduserDisplayService.toggleConfigurationMode();
	}

	private async initTermsData() {
		if (this.user?.Type === UserTypeConstant.Customer && !this.user?.AdminOnChain && this.user?.ContactCode) {
			this.subscriptions.push(this.termsService.termsAccepted.asObservable().subscribe(accepted => {
				this.showNotificationBullet = this.showNotification(accepted);
			}));

			this.termsService.getUserTermsSignedData(this.user.CustomerNo)
				.then(userTermsData => {
					this.showTermsInDropdownMenu = userTermsData.TermsDocumentLegal !== '';
					this.showNotificationBullet = this.showNotification(userTermsData.TermsSigned);
				})
				.catch(error => {
					console.error('Error retrieving terms information: ', error);
				});
		}
	}

	onSearchAnimationDone(event: TransitionEvent) {
		if (!this.showSearch && event.target === this.searchElement.nativeElement) {
			this.animationImageState = 'start'
		}
	}

	onImageAnimationDone() {
		if (this.showSearch) {
			this.searchElement.nativeElement.focus();
			this.animationSearchState = 'end'
			this.animationCloseSearchState = 'end'
		}
	}

	toggleSearch(iconClicked = false) {
		if (iconClicked && this.showSearch) {
			const enterEvent = new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter' });
			this.handleKeyUp(enterEvent)
		}
		else {
			if (!this.showSearch) {
				this.animationImageState = 'end';
			}
			else {
				this.animationSearchState = 'start'
				this.animationCloseSearchState = 'start'
				this.searchTerm.next(undefined);

				this.searchElement.nativeElement.blur();
			}

			this.showSearch = !this.showSearch;
		}
	}

	private showNotification(termsAccepted: boolean): boolean {
		return this.showTermsInDropdownMenu && !termsAccepted;
	}

	ngOnDestroy() {
		this.subscriptions.forEach(x => x.unsubscribe());
	}

	openMenu() {
		this.optionsDropDownService.toggleDropdownState();

		this.handleAnim('options');
	}

	shouldShowDisplayEnduser(): boolean {
		return this.router.url != "/bag";
	}

	handleAnim(icon: string) {
		setTimeout(() => {
			this.activeIcon = icon;
		}, 1);

		setTimeout(() => {
			this.activeIcon = '';
		}, 1);
	}

	async goToKering() {
		await this.navigateService.navigateToKeringEyewareBrands();
	}

	async goToFavorites() {
		let group = new InspirationGroupModel();
		group.Id = 5;
		group.Name = 'HOME.INSPIRATION.SCREENSHOW.FAVORITE';

		const user = await this.userService.GetUser();
		await this.googleAnalyticsService.eventEmitter('inspirations', 'engagement', `${user?.Uuid} clicked ${group.Name}`, 30);


		this.activateTitanium()

		await this.navigateService.navigateToFeatured(group);
	}

	handleMenuChange($event: any) {
		this.isMenuOpen = $event === 'expanded';
	}

	private activateTitanium() {
		if (this.brandModeService.currentBrandValue() !== BrandEnum.Titanium) {
			this.brandModeService.toggleBrandMode(BrandEnum.Titanium);
		}

		if (this.appStore.ref()) {
			this.themeService.updateTheme('titanium', this.appStore.ref());
		}
	}

	goToHome() {
		this.activateTitanium();

		this.router.navigate(['/']);
	}

	goToFilter() {
		this.activateTitanium();

		this.router.navigate(['/filter']);
	}

	goToCustomerSelector() {
		if (this.addToBagInProgress) {
			return;
		}

		this.activateTitanium();

		this.router.navigate(['/customer']);
	}

	handleKeyUp($event: KeyboardEvent) {
		if (($event.code === 'Enter' || $event.code === 'NumpadEnter') && this.searchTerm) {
			const searchTerm = this.searchTerm.value;
			this.searchTerm.next(undefined); // Clear search term to cancel debounce that triggers search again after 500 ms.

			if (searchTerm) {
				this.searchService.search(searchTerm);

				this.router.navigate(['/search', searchTerm], { onSameUrlNavigation: 'reload' });
			}
		}
	}

}
